import { useCallback, useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Skeleton } from "../../components/ui/skeleton";
import { Switch } from "../../components/ui/switch";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import StickyHeading from "../../components/common/StickyHeading";
import {
  Sparkles, Pill, Plus, Pencil, Archive, RotateCcw, Loader2, Save, History,
} from "lucide-react";
import { toast } from "sonner";
import { isPendingMasterChange, sentForApprovalMessage } from "../../lib/masterChange";
import MasterChangeDestination from "../../components/common/MasterChangeDestination";

/**
 * Product Master — the Territory Master pattern (F445/F446), one nav item away.
 *
 * ARCHIVE, NOT DELETE, and RENAME IN PLACE. What this page used to say, in the
 * edit dialog, was: "The name can't be changed — to rename, delete and re-add."
 * That is the #78 mechanism printed as user documentation. Following it left
 * every doctor, visit and sample issuance naming the old row on a value the
 * validator refuses, silently. The rename is now a real rename that carries
 * its references and its history with it, and Delete is gone from the screen
 * entirely — the backend has no path that removes a row either.
 *
 * THE REFERENCE COUNT IS SHOWN BEFORE THE ARCHIVE, fetched when the
 * confirmation opens, because "what points at this?" is exactly the question
 * the old hard Delete never asked.
 *
 * PRICES ARE EMPTY ON PURPOSE. D5/D6/D7 numbers arrive before 1 September.
 * Every price renders "Not set" until somebody types a real one — never as ₹0,
 * because a sample valued at ₹0 is a compliance claim about a free good rather
 * than a blank field. `value == null` is the test everywhere below; `!value`
 * would call a real 0.00 empty.
 *
 * THE PRICE FIELDS ARE SERVED, NOT LISTED HERE. This page used to keep four
 * copies of the list — the money keys in one array, an `EMPTY_PRICE` object
 * naming all eight, the payload keys spelled out again inside `pricePayload`,
 * and the two P2 slot names a fourth time. A form assembling the list it offers
 * somewhere other than where the validator assembles the list it accepts is
 * #78 and #83 for the third time. `price_field_list` comes from
 * `GET /settings/products/catalog`, which is
 * `product_master.price_field_descriptors` fed the stored P2 configuration —
 * the same function the write path is held to by
 * `backend/tests/test_pricing_field_contract.py`.
 *
 * THERE IS NO FALLBACK LIST. A fallback is used at precisely the moment the
 * real list could not be loaded, which is the worst moment to guess. If the
 * list is missing, the pricing block says so and offers no boxes at all.
 */

const NOT_SET = "Not set";

const EMPTY_FORM = {
  name: "", pack_size: "", composition: "", hsn_code: "", dpco_scheduled: false,
  effective_from: "",
};

/** The price boxes to draw right now.
 *
 * `requires_dpco` is the descriptor's copy of the rule `_validate_price`
 * refuses on — a ceiling belongs only to a scheduled medicine. The page used to
 * decide that for itself while keeping the loaded ceiling in form state and
 * sending it regardless, so unticking "DPCO scheduled" on a medicine that had a
 * ceiling produced a 400 about a field no longer on screen. One rule now: a
 * field that is not drawn is not sent.
 */
function applicableFields(fields, dpcoScheduled) {
  return fields.filter((f) => !f.requires_dpco || dpcoScheduled);
}

/** `""` -> null, never 0. A blank box is "not set"; only a typed 0 is zero. */
function numberOrNull(raw) {
  const text = String(raw ?? "").trim();
  if (text === "") return null;
  const value = Number(text);
  return Number.isFinite(value) ? value : null;
}

/** A stored value back into the form. `null` -> "", `0` -> "0". */
function toField(value) {
  return value === null || value === undefined ? "" : String(value);
}

export function NotSet() {
  return <span className="text-callisto-muted italic" data-testid="not-set">{NOT_SET}</span>;
}

export function Money({ value }) {
  if (value === null || value === undefined) return <NotSet />;
  return (
    <span className="tabular-nums">
      ₹{Number(value).toLocaleString("en-IN", {
        minimumFractionDigits: 2, maximumFractionDigits: 2,
      })}
    </span>
  );
}

function Rate({ value }) {
  if (value === null || value === undefined) return <NotSet />;
  return <span className="tabular-nums">{Number(value)}%</span>;
}

export default function ProductMasterPage() {
  const [items, setItems] = useState([]);
  // The served descriptor list. `[]` until the catalogue loads, and `[]` is
  // also what a response without it leaves behind — deliberately, so a missing
  // list produces "no price fields, and it says so" rather than a guessed set.
  const [priceFields, setPriceFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null); // null = add mode
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);

  const [archiveTarget, setArchiveTarget] = useState(null);
  const [archiveRefs, setArchiveRefs] = useState(null); // null = still loading
  const [archiving, setArchiving] = useState(false);

  const [historyFor, setHistoryFor] = useState(null);
  const [history, setHistory] = useState(null);

  const load = useCallback(async () => {
    setError("");
    try {
      const { data } = await api.get("/settings/products/catalog");
      setItems(data.items || []);
      setPriceFields(data.price_field_list || []);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  /** Blank boxes for every price field currently offered. */
  function blankPrices() {
    return Object.fromEntries(priceFields.map((f) => [f.key, ""]));
  }

  function openAdd() {
    setEditing(null);
    setForm({ ...EMPTY_FORM, ...blankPrices() });
    setDialogOpen(true);
  }

  function openEdit(product) {
    setEditing(product);
    const price = product.price || {};
    setForm({
      name: product.name,
      pack_size: product.pack_size || "",
      composition: product.composition || "",
      hsn_code: product.hsn_code || "",
      dpco_scheduled: !!product.dpco_scheduled,
      // The effective date is deliberately BLANK on open. A price revision is a
      // new dated row, so re-saving the form must not silently re-stamp the
      // price in force with today's date — the user says when it takes effect.
      effective_from: "",
      // Every offered field, and only those. A value stored under a key the
      // server no longer offers — a slot switched back off — is kept in the
      // record and simply not editable here, which is the same treatment a
      // retired medicine's past visits get.
      ...Object.fromEntries(priceFields.map((f) => [f.key, toField(price[f.key])])),
    });
    setDialogOpen(true);
  }

  function pricePayload() {
    // Only sent when a date is given. A price without an effective date is not
    // effective-dated, it is just a number, and the backend refuses it.
    if (!form.effective_from) return null;
    const price = { effective_from: form.effective_from };
    // Only the fields actually DRAWN. A box the user cannot see is a box they
    // cannot clear, and sending its stale value is how de-scheduling a DPCO
    // medicine came to be refused for a ceiling that was no longer on screen.
    applicableFields(priceFields, form.dpco_scheduled).forEach((f) => {
      price[f.key] = numberOrNull(form[f.key]);
    });
    return price;
  }

  async function save() {
    const name = form.name.trim();
    if (!name) return toast.error("Medicine name is required");
    if (!form.pack_size.trim()) return toast.error("Pack size is required");

    setSaving(true);
    try {
      const price = pricePayload();
      const body = {
        name,
        pack_size: form.pack_size.trim(),
        composition: form.composition.trim(),
        hsn_code: form.hsn_code.trim(),
        dpco_scheduled: !!form.dpco_scheduled,
        ...(price ? { price } : {}),
      };
      const { data } = editing
        ? await api.put(`/settings/products/${encodeURIComponent(editing.name)}`, body)
        : await api.post("/settings/products", body);
      if (isPendingMasterChange(data)) {
        toast.success(sentForApprovalMessage(data));
      } else {
        toast.success(editing ? "Medicine updated" : "Medicine added");
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  }

  async function openArchive(product) {
    setArchiveTarget(product);
    setArchiveRefs(null);
    try {
      const { data } = await api.get(
        `/settings/products/${encodeURIComponent(product.name)}/references`);
      setArchiveRefs(data);
    } catch (e) {
      // The count is the whole point of the confirmation, so a failure to read
      // it is shown rather than defaulted to zero — "nothing references this"
      // is the one answer that must never be a guess.
      setArchiveRefs({ error: formatApiErrorDetail(e.response?.data?.detail) || e.message });
    }
  }

  async function confirmArchive() {
    if (!archiveTarget) return;
    setArchiving(true);
    try {
      const { data } = await api.post(
        `/settings/products/${encodeURIComponent(archiveTarget.name)}/archive`);
      toast.success(isPendingMasterChange(data) ? sentForApprovalMessage(data) : "Medicine archived");
      setArchiveTarget(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      setArchiveTarget(null);
    } finally {
      setArchiving(false);
    }
  }

  async function restore(product) {
    try {
      const { data } = await api.post(
        `/settings/products/${encodeURIComponent(product.name)}/restore`);
      toast.success(isPendingMasterChange(data) ? sentForApprovalMessage(data) : "Medicine restored");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }

  async function openHistory(product) {
    setHistoryFor(product);
    setHistory(null);
    try {
      const { data } = await api.get(
        `/settings/products/${encodeURIComponent(product.name)}/prices`);
      setHistory(data.items || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      setHistory([]);
    }
  }

  const activeCount = items.filter((p) => !p.archived).length;
  const incompleteCount = items.filter((p) => p.needs_completion).length;
  // The spare P2 columns, as the server offers them. Not a list of slot names
  // kept here — a slot the Director has not switched on is not in the served
  // descriptors at all, and the write path refuses a value in one, so offering
  // it would put a box on screen whose every entry is rejected.
  const extraSlots = priceFields.filter((f) => f.slot);
  const labelFor = (key) => priceFields.find((f) => f.key === key)?.label || key;

  // THE STANDING RULE: where a computation depends on an absent value, say so
  // on the surface and NAME the input. D7 values a sample issuance at the cost
  // price of its issuance date, so a medicine with no cost cannot have its
  // samples valued at all — and the honest report of that is a count of the
  // medicines concerned, never a ₹0 total. The count is derived from the rows
  // just fetched rather than asserted from a document, because "how many are
  // still unpriced" stops being true the day somebody fills one in.
  const unpricedForSamples = items.filter((p) => !p.archived && p.price?.cost == null);

  return (
    <div className="space-y-6" data-testid="product-master-page">
      <StickyHeading
        icon={Sparkles}
        label="Product Master"
        title="Medicines"
        subtitle={`${activeCount} medicine${activeCount === 1 ? "" : "s"} in the pickers. Archived medicines stay in every past record. ${incompleteCount} still need details.`}
        testId="product-master-heading"
        actions={
          <Button
            onClick={openAdd}
            className="h-11 bg-callisto-navy hover:bg-callisto-navy/90 text-white shrink-0"
            data-testid="product-add"
          >
            <Plus size={16} className="mr-1.5" /> Add Medicine
          </Button>
        }
      />

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 text-red-800 px-3 py-2.5 text-sm">{error}</div>
      )}

      <p className="text-xs text-callisto-muted" data-testid="product-pricing-note">
        Prices are effective-dated: saving a price writes a new row from the date
        you give, and the old one keeps valuing the claims filed against it.
        Nothing is overwritten. Prices left blank show as “{NOT_SET}” — they are
        never treated as zero.
      </p>

      {!loading && unpricedForSamples.length > 0 && (
        <div
          className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-900"
          data-testid="product-sample-valuation-gap"
        >
          <span className="font-semibold">
            Sample valuation is not computable for {unpricedForSamples.length} of{" "}
            {activeCount} medicine{activeCount === 1 ? "" : "s"} — {labelFor("cost")} not configured.
          </span>{" "}
          A sample issuance is valued at the {labelFor("cost").toLowerCase()} price
          in force on the day it was issued. Until a price is entered, those
          issuances record their value as unset and no total that includes them
          can be produced — the totals say so rather than leaving the medicine out
          or counting it as nothing.
        </div>
      )}

      {loading ? (
        <Skeleton className="h-64 w-full rounded-xl" />
      ) : items.length === 0 ? (
        <div className="bg-white border border-callisto rounded-xl p-10 text-center">
          <Pill className="mx-auto h-8 w-8 text-callisto-muted" />
          <p className="mt-3 text-sm font-semibold text-callisto-navy">No medicines yet</p>
          <p className="text-xs text-callisto-muted">Use “Add Medicine” to build the product master.</p>
        </div>
      ) : (
        <div className="bg-white border border-callisto rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left" data-testid="product-table">
              <thead>
                <tr className="border-b border-callisto bg-callisto-app text-[11px] uppercase tracking-wider text-callisto-muted">
                  <th className="px-4 py-3 font-semibold">Medicine</th>
                  <th className="px-4 py-3 font-semibold whitespace-nowrap">Pack size</th>
                  <th className="px-4 py-3 font-semibold whitespace-nowrap">HSN</th>
                  {/* Labelled from the served descriptors, so a field cannot be
                      called one thing here and another in the completion
                      remark that tells the user to go and fill it in. */}
                  <th className="px-4 py-3 font-semibold whitespace-nowrap">{labelFor("gst_rate")}</th>
                  <th className="px-4 py-3 font-semibold whitespace-nowrap">{labelFor("mrp")}</th>
                  {/* D7: sample value = cost price, so cost is a column, not a
                      detail hidden behind an edit dialog. */}
                  <th className="px-4 py-3 font-semibold whitespace-nowrap">{labelFor("cost")}</th>
                  {extraSlots.map((f) => (
                    <th key={f.key} className="px-4 py-3 font-semibold whitespace-nowrap">
                      {f.label}
                    </th>
                  ))}
                  <th className="px-4 py-3 font-semibold text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-callisto">
                {items.map((p) => (
                  <tr
                    key={p.name}
                    className={`align-top ${p.archived ? "opacity-60" : ""}`}
                    data-testid={`product-row-${p.name}`}
                  >
                    <td className="px-4 py-3 font-semibold text-callisto-navy">
                      <div className="whitespace-nowrap">
                        {p.name}
                        {p.archived && (
                          <span
                            className="ml-2 text-[10px] font-bold uppercase tracking-wider text-orange-700"
                            data-testid={`product-archived-${p.name}`}
                          >
                            Archived
                          </span>
                        )}
                      </div>
                      {p.needs_completion && (
                        <p
                          className="mt-1 text-[11px] font-normal text-amber-700"
                          data-testid={`product-needs-completion-${p.name}`}
                        >
                          {p.completion_remark}
                        </p>
                      )}
                      {p.former_names?.length > 0 && (
                        <p className="mt-1 text-[11px] font-normal text-callisto-muted">
                          Previously {p.former_names.join(", ")}
                        </p>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-callisto-body whitespace-nowrap">
                      {p.pack_size || <NotSet />}
                    </td>
                    <td className="px-4 py-3 text-sm text-callisto-body whitespace-nowrap">
                      {p.hsn_code || <NotSet />}
                    </td>
                    <td className="px-4 py-3 text-sm text-callisto-body whitespace-nowrap">
                      <Rate value={p.price?.gst_rate} />
                    </td>
                    <td className="px-4 py-3 text-sm text-callisto-body whitespace-nowrap"
                        data-testid={`product-mrp-${p.name}`}>
                      <Money value={p.price?.mrp} />
                    </td>
                    <td className="px-4 py-3 text-sm text-callisto-body whitespace-nowrap"
                        data-testid={`product-cost-${p.name}`}>
                      <Money value={p.price?.cost} />
                    </td>
                    {extraSlots.map((f) => (
                      <td key={f.key} className="px-4 py-3 text-sm text-callisto-body whitespace-nowrap">
                        <Money value={p.price?.[f.key]} />
                      </td>
                    ))}
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="outline" size="sm"
                          onClick={() => openHistory(p)}
                          className="h-8 border-callisto text-[11px]"
                          data-testid={`product-prices-${p.name}`}
                        >
                          <History size={12} className="mr-1" /> Prices
                        </Button>
                        {p.archived ? (
                          <Button
                            variant="outline" size="sm"
                            onClick={() => restore(p)}
                            className="h-8 border-callisto text-callisto-navy text-[11px]"
                            data-testid={`product-restore-${p.name}`}
                          >
                            <RotateCcw size={12} className="mr-1" /> Restore
                          </Button>
                        ) : (
                          <>
                            <Button
                              variant="outline" size="sm"
                              onClick={() => openEdit(p)}
                              className="h-8 border-callisto text-[11px]"
                              data-testid={`product-edit-${p.name}`}
                            >
                              <Pencil size={12} className="mr-1" /> Edit
                            </Button>
                            <Button
                              variant="outline" size="sm"
                              onClick={() => openArchive(p)}
                              className="h-8 border-orange-200 text-orange-700 hover:bg-orange-50 text-[11px]"
                              data-testid={`product-archive-${p.name}`}
                            >
                              <Archive size={12} className="mr-1" /> Archive
                            </Button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Add / Edit dialog — the name is EDITABLE, and renaming keeps history. */}
      <Dialog open={dialogOpen} onOpenChange={(o) => { if (!o) setDialogOpen(false); }}>
        <DialogContent className="max-h-[85vh] overflow-y-auto" data-testid="product-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? `Edit ${editing.name}` : "Add medicine"}</DialogTitle>
            <DialogDescription data-testid="product-dialog-description">
              {editing
                ? "Renaming here is a real rename: past visits, sample issuances and doctor lists move to the new name, and the old one is kept in the medicine’s history. Leave a price blank and it stays “Not set” — it is never recorded as zero."
                : "Add a medicine to the product master. It appears in the DCR and sample dropdowns once the change is approved. Prices can be left empty and filled in later."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-callisto-navy">Name</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="e.g., CALIRAB DSR Cap"
                className="h-11 mt-1.5 focus-visible:ring-callisto-gold focus-visible:ring-offset-0"
                data-testid="product-form-name"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-callisto-navy">Pack size</Label>
                <Input
                  value={form.pack_size}
                  onChange={(e) => setForm((f) => ({ ...f, pack_size: e.target.value }))}
                  placeholder="e.g., 10x10 Tab"
                  className="h-11 mt-1.5 focus-visible:ring-callisto-gold focus-visible:ring-offset-0"
                  data-testid="product-form-pack-size"
                />
              </div>
              <div>
                <Label className="text-callisto-navy">HSN code</Label>
                <Input
                  value={form.hsn_code}
                  onChange={(e) => setForm((f) => ({ ...f, hsn_code: e.target.value }))}
                  placeholder="Leave blank if not known yet"
                  className="h-11 mt-1.5 focus-visible:ring-callisto-gold focus-visible:ring-offset-0"
                  data-testid="product-form-hsn"
                />
              </div>
            </div>
            <div>
              <Label className="text-callisto-navy">Composition</Label>
              <Textarea
                value={form.composition}
                onChange={(e) => setForm((f) => ({ ...f, composition: e.target.value }))}
                placeholder="Each tablet contains…"
                rows={3}
                className="mt-1.5 focus-visible:ring-callisto-gold focus-visible:ring-offset-0"
                data-testid="product-form-composition"
              />
            </div>

            <div className="rounded-lg border border-callisto p-3 space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-callisto-gold">
                  Pricing
                </p>
                <div className="flex items-center gap-2">
                  <Label className="text-[11px] text-callisto-body">DPCO scheduled</Label>
                  <Switch
                    checked={form.dpco_scheduled}
                    onCheckedChange={(v) => setForm((f) => ({ ...f, dpco_scheduled: !!v }))}
                    data-testid="product-form-dpco"
                  />
                </div>
              </div>
              {priceFields.length === 0 ? (
                // NO FALLBACK LIST. A guessed set of price boxes is wrong in
                // exactly the case it would be used — the fetch failed — and a
                // box the server does not offer is one the write path refuses.
                <p className="text-[11px] text-red-700" data-testid="product-form-price-fields-missing">
                  The price fields could not be loaded, so no price can be
                  entered here. Everything else on this form still saves.
                </p>
              ) : (
                <>
                  <p className="text-[11px] text-callisto-muted">
                    Give an effective date to record a price. Anything left blank is
                    stored as “{NOT_SET}”, not as zero.
                  </p>
                  <div>
                    <Label className="text-callisto-navy">Effective from</Label>
                    <Input
                      type="date"
                      value={form.effective_from}
                      onChange={(e) => setForm((f) => ({ ...f, effective_from: e.target.value }))}
                      className="h-11 mt-1.5 focus-visible:ring-callisto-gold focus-visible:ring-offset-0"
                      data-testid="product-form-effective-from"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    {applicableFields(priceFields, form.dpco_scheduled).map((field) => (
                      <div key={field.key}>
                        <Label className="text-callisto-navy">
                          {field.label}{field.kind === "percent" ? " (%)" : ""}
                        </Label>
                        <Input
                          inputMode="decimal"
                          value={form[field.key] ?? ""}
                          onChange={(e) => setForm((f) => ({ ...f, [field.key]: e.target.value }))}
                          placeholder={NOT_SET}
                          className="h-11 mt-1.5 focus-visible:ring-callisto-gold focus-visible:ring-offset-0"
                          data-testid={`product-form-${field.key}`}
                        />
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-callisto">
              Cancel
            </Button>
            <Button
              onClick={save}
              disabled={saving}
              className="bg-callisto-navy hover:bg-callisto-navy/90 text-white"
              data-testid="product-form-save"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><Save size={14} className="mr-1.5" /> {editing ? "Save changes" : "Add medicine"}</>)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Archive confirm — the reference count is read BEFORE the action. */}
      <AlertDialog open={!!archiveTarget} onOpenChange={(o) => !o && setArchiveTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archive “{archiveTarget?.name}”?</AlertDialogTitle>
            <AlertDialogDescription data-testid="product-archive-description">
              {/* THE DESTINATION FIRST. This is a filing, not an archive — the
                  sentence below describes what APPROVAL will cause. */}
              <MasterChangeDestination testId="product-archive-destination" />
              <span className="block mt-2">
                Nothing changes yet. Once approved it leaves the DCR and sample
                dropdowns and stays in every past record. Nothing is deleted, and you
                can restore it at any time.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div
            className="rounded-lg border border-callisto bg-callisto-app px-3 py-2 text-sm text-callisto-body"
            data-testid="product-archive-references"
          >
            {archiveRefs === null
              ? "Counting what references this medicine…"
              : archiveRefs.error
                ? `Could not read the reference count: ${archiveRefs.error}`
                : archiveRefs.sentence}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={archiving}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); confirmArchive(); }}
              disabled={archiving}
              className="bg-orange-600 hover:bg-orange-700"
              data-testid="product-archive-confirm"
            >
              {archiving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Archive"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Price history — nothing is ever overwritten, so this is a record. */}
      <Dialog open={!!historyFor} onOpenChange={(o) => !o && setHistoryFor(null)}>
        <DialogContent data-testid="product-price-history">
          <DialogHeader>
            <DialogTitle>Prices — {historyFor?.name}</DialogTitle>
            <DialogDescription>
              Every price ever recorded, newest first. A revision adds a row; it
              never replaces one, so a claim is always valued at the price of its
              own date.
            </DialogDescription>
          </DialogHeader>
          {history === null ? (
            <Skeleton className="h-24 w-full rounded-lg" />
          ) : history.length === 0 ? (
            <p className="text-sm text-callisto-muted" data-testid="product-price-history-empty">
              No price has been recorded for this medicine yet.
            </p>
          ) : (
            <div className="overflow-x-auto">
              {/* EVERY recorded field, not a chosen four. The history showed
                  MRP/PTS/PTR/cost/GST only, so a superseded DPCO CEILING — a
                  gazetted number, and the one figure most worth being able to
                  read back — was invisible, as was any value entered into a
                  spare slot. A history that drops columns is not a history.
                  The DPCO ceiling is shown unconditionally here, unlike on the
                  form: what was recorded stays readable even after the medicine
                  is de-scheduled. */}
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="text-[11px] uppercase tracking-wider text-callisto-muted">
                    <th className="py-2 pr-3 font-semibold">Effective from</th>
                    {priceFields.map((f) => (
                      <th key={f.key} className="py-2 pr-3 font-semibold">{f.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-callisto">
                  {history.map((row) => (
                    <tr key={row.id} data-testid={`product-price-row-${row.effective_from}`}>
                      <td className="py-2 pr-3 whitespace-nowrap">{row.effective_from}</td>
                      {priceFields.map((f) => (
                        <td key={f.key} className="py-2 pr-3 whitespace-nowrap">
                          {f.kind === "percent"
                            ? <Rate value={row[f.key]} />
                            : <Money value={row[f.key]} />}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
